/**
 * "Cidades no caminho": as sedes municipais que o contorno previsto do sistema
 * cobre, e a partir de que horário. É só o que a previsão diz — o contorno do
 * pyForTraCC transladado —, sem margem nem distância calculada aqui: a sede
 * entra quando cai DENTRO de um contorno (o observado, ou o de uma
 * antecedência), e fica com a primeira em que isso acontece.
 *
 * As sedes vêm do mesmo arquivo da camada "Cidades" (`public/data/sedes.geojson`,
 * pelo carregador dos vetores, já na projeção do mapa), sem ligar a camada; e
 * só são pedidas quando a ficha de um sistema tem previsão.
 */
import { containsCoordinate } from 'ol/extent';
import type Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import type Polygon from 'ol/geom/Polygon';
import type VectorSource from 'ol/source/Vector';
import { ensureDataSource } from '../vectors/loader';
import type { PassoPrevisto } from './registro';

export interface Sede {
  nome: string;
  uf: string;
  pop: number;
  /** Projeção do mapa. */
  ponto: [number, number];
}

export interface CidadeNoCaminho {
  nome: string;
  uf: string;
  /** Primeira antecedência (min) em que o contorno cobre a sede; 0 = já sob o sistema observado. */
  h: number;
}

/**
 * As sedes cobertas pelo contorno observado (h = 0) ou pelos previstos, cada
 * uma com a primeira antecedência que a cobre; da mais próxima no tempo para a
 * mais distante e, no mesmo horário, da mais populosa para a menos.
 */
export function cidadesNoCaminho(
  passos: readonly Pick<PassoPrevisto, 'h' | 'geometria'>[],
  observado: Polygon | null,
  sedes: readonly Sede[],
): CidadeNoCaminho[] {
  const contornos = [...(observado ? [{ h: 0, geometria: observado }] : []), ...[...passos].sort((a, b) => a.h - b.h)].map((c) => ({
    h: c.h,
    geometria: c.geometria,
    extensao: c.geometria.getExtent(),
  }));
  if (!contornos.length) return [];
  const achadas: (CidadeNoCaminho & { pop: number })[] = [];
  for (const s of sedes) {
    const c = contornos.find((x) => containsCoordinate(x.extensao, s.ponto) && x.geometria.intersectsCoordinate(s.ponto));
    if (c) achadas.push({ nome: s.nome, uf: s.uf, h: c.h, pop: s.pop });
  }
  return achadas.sort((a, b) => a.h - b.h || b.pop - a.pop || a.nome.localeCompare(b.nome, 'pt-BR')).map(({ nome, uf, h }) => ({ nome, uf, h }));
}

/**
 * As primeiras `max` cidades, juntas por horário (uma linha por antecedência,
 * como a ficha as mostra), e quantas ficam de fora ("e mais N").
 */
export function cidadesPorHorario(cidades: readonly CidadeNoCaminho[], max = 5): { grupos: { h: number; nomes: string[] }[]; mais: number } {
  const { mostradas, mais } = resumoDasCidades(cidades, max);
  const grupos: { h: number; nomes: string[] }[] = [];
  for (const c of mostradas) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.h === c.h) ultimo.nomes.push(c.nome);
    else grupos.push({ h: c.h, nomes: [c.nome] });
  }
  return { grupos, mais };
}

/** As primeiras `max` cidades e quantas ficam de fora ("e mais N"). */
export function resumoDasCidades<T>(cidades: readonly T[], max = 5): { mostradas: T[]; mais: number } {
  return { mostradas: cidades.slice(0, max), mais: Math.max(0, cidades.length - max) };
}

/** As sedes de uma fonte já carregada (pontos com nome); o resto fica de fora. */
export function sedesDaFonte(feicoes: readonly Feature[]): Sede[] {
  const out: Sede[] = [];
  for (const f of feicoes) {
    const g = f.getGeometry();
    const nome = f.get('nome') as unknown;
    if (!(g instanceof Point) || typeof nome !== 'string' || !nome) continue;
    const [x, y] = g.getCoordinates();
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    const uf = f.get('uf') as unknown;
    const pop = f.get('pop') as unknown;
    out.push({ nome, uf: typeof uf === 'string' ? uf : '', pop: typeof pop === 'number' ? pop : 0, ponto: [x, y] });
  }
  return out;
}

let pedido: Promise<Sede[] | null> | null = null;

/** As sedes, baixadas uma vez por sessão (null se o arquivo não veio; uma nova chamada tenta de novo). */
export function carregarSedes(): Promise<Sede[] | null> {
  pedido ??= ensureDataSource('sedes').then((source: VectorSource | null) => {
    if (!source) {
      pedido = null;
      return null;
    }
    return sedesDaFonte(source.getFeatures());
  });
  return pedido;
}
